import { useEffect, useRef, useState } from "react";
import { AnimatePresence } from "motion/react";
import type { MatchPlayer } from "@minigames/shared";
import { FloatingReaction } from "./ChatPanel.js";

/** Порог, ниже которого часы становятся тревожными. */
const LOW_TIME_MS = 20_000;

function fmt(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  // Под 20 секунд показываем десятые — так виднее, что время горит.
  if (ms < LOW_TIME_MS) {
    const tenths = Math.max(0, Math.floor((ms % 1000) / 100));
    return `${m}:${String(s).padStart(2, "0")}.${tenths}`;
  }
  return `${m}:${String(s).padStart(2, "0")}`;
}

interface Props {
  player: MatchPlayer | null;
  /** Реакция, которую этот игрок только что отправил. */
  reaction?: string | null;
  /** Остаток по последнему снимку сервера. */
  remainingMs: number | null;
  /** Идут ли часы у этого игрока. */
  running: boolean;
  /** Момент серверного снимка (в наших часах). */
  since: number | null;
}

/**
 * Планка игрока с часами.
 *
 * Часы тикают локально, но это лишь ИНТЕРПОЛЯЦИЯ от последнего серверного
 * снимка. Ноль на экране ничего не решает — падение флажка объявляет сервер.
 */
export function PlayerBar({ player, remainingMs, running, since, reaction }: Props) {
  const [now, setNow] = useState(() => performance.now());
  const raf = useRef<number | null>(null);

  useEffect(() => {
    if (!running) return;
    const tick = () => {
      setNow(performance.now());
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => {
      if (raf.current !== null) cancelAnimationFrame(raf.current);
    };
  }, [running]);

  const shown =
    remainingMs === null
      ? null
      : running && since !== null
        ? Math.max(0, remainingMs - (now - since))
        : remainingMs;

  const low = shown !== null && shown < LOW_TIME_MS;
  const name = player?.firstName ?? player?.username ?? "Соперник";
  const initial = (name[0] ?? "?").toUpperCase();

  return (
    <div className="player">
      <span className="avatar-wrap">
        {player?.photoUrl ? (
          <img className="avatar" src={player.photoUrl} alt="" />
        ) : (
          <div className="avatar">{initial}</div>
        )}
        <AnimatePresence>
          {reaction && <FloatingReaction key={reaction + String(remainingMs)} emoji={reaction} />}
        </AnimatePresence>
      </span>
      <span className={`presence${player?.online ? " on" : ""}`} />
      <span className="name">{name}</span>
      {shown !== null && (
        <span className={`clock${running ? " active" : ""}${low ? " low" : ""}`}>{fmt(shown)}</span>
      )}
    </div>
  );
}
